/**
 * RollOption (Foundry: rule-element/roll-option/rule-element.ts): set a roll option in a domain,
 * optionally as a toggle the player controls. Suboption configs from Foundry's CONFIG are not
 * available here; a RollOption with such a config is recorded as a problem.
 */
import { isValidPredicate, testPredicate, type RawPredicate } from "../predicate";
import { cleanRuleValue, resolveInjectedProperties, resolveValue } from "../resolve";
import type { EngineState, RollOptionToggle } from "../state";
import { sluggifyCamel } from "../slug";
import { AE_LIKE_PHASES, CHANGE_MODE_DEFAULT_PRIORITIES, type AELikePhase } from "./ae-like";
import type { RuleHandler, RuleInstance } from "./base";

interface Suboption { label: string; value: string; predicate: RawPredicate }

interface RollOptionData {
  domain: string;
  option: string;
  phase: AELikePhase;
  value: unknown;
  toggleable: boolean | "totm";
  suboptions: Suboption[];
  /** Suboptions drawn from a CONFIG table (`{ config: "skills" }`), expanded once the Snapshot's tables are at hand. */
  suboptionsConfig: { config: string; predicate: RawPredicate } | null;
  selection: string | null;
  alwaysActive: boolean;
  count: boolean;
  disabledIf: RawPredicate | null;
  disabledValue: boolean | undefined;
  placement: string | null;
  mergeable: boolean;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parse(rule: RuleInstance, state: EngineState): void {
  const s = rule.source;
  const domain = s.domain === undefined ? "all" : s.domain;
  if (typeof domain !== "string" || !domain) return rule.fail("domain: must be a non-empty string");
  if (typeof s.option !== "string" || !s.option) return rule.fail("option: must be a non-empty string");
  const phase = s.phase === undefined ? "applyAEs" : s.phase;
  if (!(AE_LIKE_PHASES as readonly string[]).includes(String(phase))) return rule.fail(`phase: "${String(phase)}" is not a data preparation phase`);
  const toggleable: boolean | "totm" = s.toggleable === "totm" ? "totm" : s.toggleable === true;
  if (s.toggleable !== undefined && s.toggleable !== true && s.toggleable !== false && s.toggleable !== "totm") return rule.fail("toggleable: must be a boolean or \"totm\"");
  if (s.value !== undefined && !["boolean", "string"].includes(typeof s.value)) return rule.fail("value: must be a boolean, string, or otherwise omitted");
  const value = s.value === undefined ? !toggleable : cleanRuleValue(s.value);

  let suboptions: Suboption[] = [];
  let suboptionsConfig: RollOptionData["suboptionsConfig"] = null;
  if (Array.isArray(s.suboptions)) {
    for (const entry of s.suboptions) {
      if (!isPlainObject(entry) || typeof entry.value !== "string" || typeof entry.label !== "string") return rule.fail("suboptions: malformed entry");
      if (entry.predicate !== undefined && !isValidPredicate(entry.predicate)) return rule.fail("suboptions: malformed predicate");
      suboptions.push({ label: entry.label, value: entry.value, predicate: (entry.predicate as RawPredicate | undefined) ?? [] });
    }
  } else if (isPlainObject(s.suboptions)) {
    // Foundry reads CONFIG.PF2E[config]; the Snapshot ships those tables (docs/design.md, Source data).
    const config = typeof s.suboptions.config === "string" ? s.suboptions.config : "";
    if (!config) return rule.fail("suboptions: config must be a table name");
    if (s.suboptions.predicate !== undefined && !isValidPredicate(s.suboptions.predicate)) return rule.fail("suboptions: malformed predicate");
    const table = state.catalogue?.table(config);
    if (!table) return rule.fail(`suboptions: the Snapshot has no "${config}" table`);
    suboptionsConfig = { config, predicate: (s.suboptions.predicate as RawPredicate | undefined) ?? [] };
    suboptions = Object.entries(table).map(([value, label]) => ({ label, value, predicate: [] }));
  } else if (s.suboptions !== undefined) {
    return rule.fail("suboptions: must be an array");
  }
  const subOptionsIsEmpty = suboptions.length === 0;
  if (!subOptionsIsEmpty && !toggleable) return rule.fail("suboptions: must be omitted if not toggleable");
  if (s.disabledIf !== undefined && !toggleable) return rule.fail("disabledIf: must be false if not toggleable");
  if (s.disabledIf !== undefined && !isValidPredicate(s.disabledIf)) return rule.fail("disabledIf: malformed predicate");
  if (s.count === true && toggleable) return rule.fail("count: must be false if toggleable");
  if (typeof s.disabledValue === "boolean" && (!toggleable || s.disabledIf === undefined)) return rule.fail("disabledValue: may only be included if toggleable and there is a disabledIf predicate");
  if (s.alwaysActive === true && (!toggleable || subOptionsIsEmpty)) return rule.fail("alwaysActive: must be false unless toggleable and containing suboptions");
  if (s.placement !== undefined && !toggleable) return rule.fail("placement: may only be present if toggleable");
  if (s.removeAfterRoll === true && rule.item.type !== "effect") return rule.fail("removeAfterRoll may only be used on rule elements from effect items");

  let selection = typeof s.selection === "string" && s.selection ? s.selection : null;
  if (suboptions.length > 0 && !suboptions.some((o) => o.value === selection)) selection = suboptions[0]!.value;
  if (typeof s.mergeable === "boolean" && s.mergeable) suboptions = suboptions.slice();

  const data: RollOptionData = {
    domain,
    option: s.option,
    phase: phase as AELikePhase,
    value,
    toggleable,
    suboptions,
    suboptionsConfig,
    selection,
    alwaysActive: s.alwaysActive === true,
    count: s.count === true,
    disabledIf: (s.disabledIf as RawPredicate | undefined) ?? null,
    disabledValue: typeof s.disabledValue === "boolean" ? s.disabledValue : undefined,
    placement: typeof s.placement === "string" ? s.placement : null,
    mergeable: s.mergeable === true,
  };
  rule.data = data as unknown as Record<string, unknown>;
}

/** Foundry's option sanitiser: letters, numbers, colons and hyphens only. */
function sanitiseOption(option: string): string {
  return option.replace(/[^-:\w]/g, "").replace(/:+/g, ":").replace(/-+/g, "-").trim();
}

function setOptionAndFlag(rule: RuleInstance, state: EngineState): void {
  if (rule.ignored) return;
  const data = rule.data as unknown as RollOptionData;
  const domain = resolveInjectedProperties(rule, state, data.domain);
  if (rule.ignored) return;
  const isStandardDomain = /^[-a-z0-9]+$/.test(domain) && /[a-z]/.test(domain);
  const isIdDomain = /^[a-zA-Z0-9]{16}-[-a-z0-9]+[a-z0-9]$/.test(domain);
  if (!isStandardDomain && !isIdDomain) return rule.fail("domain must be a string consisting of only lowercase letters, numbers, and hyphens.");

  const test = new Set([...state.rollOptions.get([domain]), ...rule.item.getRollOptions("parent")]);
  if (!rule.test(state, test)) return setFlag(rule, data, null, false);

  const baseOption = sanitiseOption(resolveInjectedProperties(rule, state, data.option));
  if (rule.ignored) return;
  if (!baseOption) return rule.fail("option: must be a string consisting of only letters, numbers, colons, and hyphens");

  const setOption = (selection: string | null, value: boolean): void => {
    const fullOption = selection ? `${baseOption}:${selection}` : baseOption;
    if (value) {
      state.rollOptions.set(domain, fullOption, true);
      state.rollOptions.set(domain, baseOption, true);
    } else {
      state.rollOptions.delete(domain, fullOption);
      state.rollOptions.delete(domain, baseOption);
    }
  };

  if (data.toggleable) {
    const enabled = data.disabledIf ? !testPredicate(data.disabledIf, test) : true;
    // A mergeable toggle is created once for its merge family.
    if (data.mergeable && state.toggles.some((t) => t.domain === domain && t.option === baseOption)) return;
    const suboptions = data.suboptions.filter((o) => {
      const predicate = data.suboptionsConfig?.predicate.length ? resolveInjectedProperties(rule, state, structuredClone(data.suboptionsConfig.predicate), { injectables: { choice: { value: o.value, label: o.label } }, warn: false }) : o.predicate;
      return predicate.length === 0 || testPredicate(predicate, test);
    });
    if (data.suboptions.length > 0 && suboptions.length === 0) {
      rule.ignored = true;
      return;
    }
    const stateKey = `${domain}:${baseOption}`;
    const saved = state.toggleState[stateKey];
    // A saved selection wins; else an Answer recorded under the option's flag (Foundry keeps the two in step); else the Rule Element's own.
    const answered = rule.item.answers[sluggifyCamel(sanitiseOption(data.option))];
    const wanted = saved?.selection !== undefined && suboptions.some((o) => o.value === saved.selection)
      ? saved.selection
      : typeof answered === "string" && suboptions.some((o) => o.value === answered)
        ? answered
        : data.selection;
    const selection = suboptions.length > 0 && !suboptions.some((o) => o.value === wanted) ? suboptions[0]!.value : wanted;
    const value = data.alwaysActive
      ? true
      : !enabled && typeof data.disabledValue === "boolean"
        ? data.disabledValue
        : saved?.checked !== undefined
          ? saved.checked
          : !!resolveValue(rule, state, data.value);
    if (rule.ignored) return;
    state.toggles.push({
      itemUuid: rule.item.uuid,
      itemName: rule.item.name,
      label: rule.reducedLabel,
      placement: data.placement ?? "actions",
      domain,
      option: baseOption,
      suboptions: suboptions.map((o) => ({ label: o.label, value: o.value })),
      selection,
      alwaysActive: data.alwaysActive,
      checked: value,
      enabled,
    });
    setOption(selection, value);
    setFlag(rule, data, selection, value);
    if (selection) rule.item.specialOptions.push(`${baseOption}:${selection}`);
  } else if (data.count) {
    // The option carries a count of how many Entries set it.
    const existing = [...state.rollOptions.get([domain])].map((key) => ({ key, count: Number(new RegExp(`^${baseOption}:(\\d+)$`).exec(key)?.[1]) || 0 })).find((kc) => kc.count > 0);
    if (existing) {
      state.rollOptions.delete(domain, existing.key);
      state.rollOptions.set(domain, `${baseOption}:${existing.count + 1}`, true);
    } else {
      state.rollOptions.set(domain, `${baseOption}:1`, true);
    }
  } else {
    const value = !!resolveValue(rule, state, data.value);
    if (rule.ignored) return;
    setOption(data.selection, value);
    setFlag(rule, data, data.selection, value);
  }
}

/** Record a suboption selection as a rules selection on the item, so `{item|flags.system.rulesSelections.x}` resolves. */
function setFlag(rule: RuleInstance, data: RollOptionData, selection: string | null, value: boolean): void {
  if (!selection) return;
  const selections = ((rule.item.flags.pf2e.rulesSelections as Record<string, unknown> | undefined) ??= {});
  const flagKey = sluggifyCamel(sanitiseOption(data.option));
  if (value) selections[flagKey] = /^\d+$/.test(selection) ? Number(selection) : selection;
  else selections[flagKey] ??= null;
}

const phaseIs = (rule: RuleInstance, phase: AELikePhase): boolean => (rule.data as unknown as RollOptionData).phase === phase;

export const RollOptionHandler: RuleHandler = {
  key: "RollOption",
  priority: () => CHANGE_MODE_DEFAULT_PRIORITIES.override,
  parse,
  onApplyActiveEffects: (rule, state) => { if (phaseIs(rule, "applyAEs")) setOptionAndFlag(rule, state); },
  beforePrepareData: (rule, state) => { if (phaseIs(rule, "beforeDerived")) setOptionAndFlag(rule, state); },
  afterPrepareData: (rule, state) => { if (phaseIs(rule, "afterDerived")) setOptionAndFlag(rule, state); },
};
